import moment from 'moment';
import createLetterSeed from '../hubLetterSeed/hubLetterSeed';

(function init() {
  angular
    .module('app')
    .controller('host.views.debugTools.triggerHubSubmitGuaranteeLetterRequest.modal', [
      '$uibModalInstance',
      'abp.services.app.debugTool',
      function controller($uibModalInstance, debugToolSvc) {
        const vm = this;
        vm.saving = 0;
        vm.submittedRequest = null;
        // Two-page modal: 'form' collects the payload, 'result' replaces it with the
        // Hub response; Back returns to the form with the entered values intact.
        vm.view = 'form';

        vm.caseId = '';
        vm.type = '';
        vm.requestTimeUtc = moment();
        vm.requestNumber = '';
        vm.admissionTimeUtc = null;
        vm.dischargeTimeUtc = null;
        vm.lengthOfStay = null;
        vm.accidentTimeUtc = null;
        vm.doctorNames = '';

        // singleDatePicker mode binds the model as a bare moment (see the vendored
        // angular-daterangepicker wrapper), so models are read directly, not via .startDate.
        // Required timestamps default to now; the plugin only fires its callback when the
        // selection changes, so an empty field plus "pick today" would otherwise commit nothing.
        vm.datePickerOptions = {
          singleDatePicker: true,
          locale: {
            format: 'L',
            applyLabel: App.localize('Apply'),
            cancelLabel: App.localize('Cancel'),
          },
        };
        vm.dateTimePickerOptions = {
          singleDatePicker: true,
          timePicker: true,
          timePicker24Hour: true,
          locale: {
            format: 'L LT',
            applyLabel: App.localize('Apply'),
            cancelLabel: App.localize('Cancel'),
          },
        };
        vm.requestTimeOptions = vm.dateTimePickerOptions;
        vm.admissionTimeOptions = vm.dateTimePickerOptions;
        vm.dischargeTimeOptions = vm.dateTimePickerOptions;
        vm.accidentTimeOptions = vm.dateTimePickerOptions;
        vm.currencyCode = '';
        vm.requestedAmount = null;
        vm.depositAmount = null;
        vm.treatmentPlan = '';
        vm.diagnoses = '';
        vm.identifiers = [];
        vm.documents = [];

        vm.typeOptions = ['Initial', 'TopUp', 'Final', 'Adjusted'];

        // Seeding from a letter [HM-11245]: how each payload field fills the form, and what the
        // form posts for it. Times and amounts arrive as text and go back as text when untouched.
        const fromTime = (value) => (value ? moment(value) : null);
        const fromAmount = (value) => (value === null || value === undefined ? null : +value);
        vm.seed = createLetterSeed({
          caseId: { fromSeed: (v) => v || '', toInput: (v) => v },
          type: { fromSeed: (v) => v || '', toInput: (v) => v },
          requestTimeUtc: { fromSeed: fromTime, toInput: toUtc },
          requestNumber: { fromSeed: (v) => v || '', toInput: (v) => v || null },
          admissionTimeUtc: { fromSeed: fromTime, toInput: toUtc },
          dischargeTimeUtc: { fromSeed: fromTime, toInput: toUtc },
          lengthOfStay: { fromSeed: (v) => v ?? null, toInput: (v) => v ?? null },
          accidentTimeUtc: { fromSeed: fromTime, toInput: toUtc },
          doctorNames: { fromSeed: (v) => (v || []).join(', '), toInput: splitList },
          currencyCode: { fromSeed: (v) => v || '', toInput: (v) => v },
          requestedAmount: { fromSeed: fromAmount, toInput: (v) => v ?? null },
          depositAmount: { fromSeed: fromAmount, toInput: (v) => v ?? null },
          treatmentPlan: { fromSeed: (v) => v || '', toInput: (v) => v || null },
          diagnoses: { fromSeed: (v) => (v || []).join(', '), toInput: splitList },
          identifiers: { fromSeed: (v) => v || [], toInput: (v) => v },
          documents: { fromSeed: (v) => v || [], toInput: (v) => v },
        });
        vm.loadSeed = () =>
          vm.seed.load(vm, (input) => debugToolSvc.getHubSubmitGuaranteeLetterRequestSeed(input));
        vm.resetSeed = () => vm.seed.reset(vm);

        vm.cancel = cancel;
        vm.execute = execute;
        vm.back = back;
        vm.addIdentifier = addIdentifier;
        vm.removeIdentifier = removeIdentifier;
        vm.addDocument = addDocument;
        vm.removeDocument = removeDocument;

        function cancel() {
          $uibModalInstance.dismiss();
        }

        function back() {
          vm.view = 'form';
        }

        function addIdentifier() {
          vm.identifiers.push({ assignerId: '', type: '', value: '' });
        }

        function removeIdentifier(index) {
          vm.identifiers.splice(index, 1);
        }

        function addDocument() {
          vm.documents.push({ type: '', value: '', title: '', contentType: '' });
        }

        function removeDocument(index) {
          vm.documents.splice(index, 1);
        }

        function splitList(text) {
          return (text || '')
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item.length);
        }

        // Whole-second UTC, matching the Hub's own examples — moment's toISOString() adds
        // milliseconds, which the Hub's timestamps never carry.
        function toUtc(value) {
          return value ? moment(value).utc().format('YYYY-MM-DDTHH:mm:ss[Z]') : null;
        }

        function displayUtc(value) {
          return value ? moment.utc(value).format('YYYY-MM-DD HH:mm:ss [UTC]') : null;
        }

        function displayAmount(value, currencyCode) {
          if (value === null || value === undefined) return null;
          return currencyCode ? `${currencyCode} ${value}` : `${value}`;
        }

        // Rows with no value are dropped rather than shown blank.
        function addRow(rows, label, value) {
          if (value !== null && value !== undefined && value !== '') rows.push({ label, value });
        }

        function buildSummary(result) {
          const requestRows = [];
          addRow(requestRows, 'Request number', result.requestNumber);
          addRow(requestRows, 'Hub request ID', result.id);
          addRow(requestRows, 'Type', result.type);
          addRow(requestRows, 'Status', result.status);
          addRow(requestRows, 'Requested', displayUtc(result.requestTimeUtc));
          addRow(requestRows, 'Created', displayUtc(result.createdUtc));

          const caseRows = [];
          addRow(caseRows, 'Hub case ID', result.caseId);
          addRow(caseRows, 'Hub patient ID', result.patientId);
          addRow(caseRows, 'Hub policy ID', result.policyId);
          addRow(caseRows, 'Provider ID', result.providerId);

          const amountRows = [];
          addRow(amountRows, 'Requested amount', displayAmount(result.requestedAmount, result.currencyCode));
          addRow(amountRows, 'Deposit amount', displayAmount(result.depositAmount, result.currencyCode));

          const stayRows = [];
          addRow(stayRows, 'Admission time', displayUtc(result.admissionTimeUtc));
          addRow(stayRows, 'Discharge time', displayUtc(result.dischargeTimeUtc));
          addRow(stayRows, 'Length of stay', result.lengthOfStay);
          addRow(stayRows, 'Accident time', displayUtc(result.accidentTimeUtc));
          addRow(stayRows, 'Doctors', (result.doctorNames || []).join(', ') || null);
          addRow(stayRows, 'Diagnoses', (result.diagnoses || []).join(', ') || null);
          addRow(stayRows, 'Treatment plan', result.treatmentPlan);
          addRow(stayRows, 'Outcomes', (result.outcomes || []).length || null);

          return {
            requestNumber: result.requestNumber,
            type: result.type,
            status: result.status,
            groups: [
              { title: 'Request', rows: requestRows },
              { title: 'Case', rows: caseRows },
              { title: 'Amounts', rows: amountRows },
              { title: 'Stay & treatment', rows: stayRows },
            ].filter((group) => group.rows.length),
          };
        }

        function execute() {
          if (!App.isFormValid(vm.triggerHubSubmitGuaranteeLetterRequestForm)) return;

          // Every field is sent explicitly — unset times as null, empty collections as [] — so the
          // request matches the Hub's documented payload shape [HM-11061]. Seeded fields left
          // untouched go back exactly as seeded [HM-11245].
          const input = vm.seed.toInput(vm, {
            caseId: vm.caseId,
            type: vm.type,
            currencyCode: vm.currencyCode,
            requestedAmount: vm.requestedAmount,
            doctorNames: splitList(vm.doctorNames),
            diagnoses: splitList(vm.diagnoses),
            identifiers: vm.identifiers,
            documents: vm.documents,
            requestTimeUtc: toUtc(vm.requestTimeUtc),
            admissionTimeUtc: toUtc(vm.admissionTimeUtc),
            dischargeTimeUtc: toUtc(vm.dischargeTimeUtc),
            accidentTimeUtc: toUtc(vm.accidentTimeUtc),
            requestNumber: vm.requestNumber || null,
            treatmentPlan: vm.treatmentPlan || null,
            lengthOfStay: vm.lengthOfStay ?? null,
            depositAmount: vm.depositAmount ?? null,
          });

          vm.saving += 1;
          vm.submittedRequest = null;
          debugToolSvc
            .triggerHubSubmitGuaranteeLetterRequest(input)
            .success((result) => {
              vm.submittedRequest = buildSummary(result);
              vm.view = 'result';
              abp.notify.success(
                `Guarantee letter request ${result.requestNumber || result.id} submitted to the Hub.`
              );
            })
            .finally(() => {
              vm.saving -= 1;
            });
        }
      },
    ]);
})();
